import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { Play, Link as LinkIcon, Upload, X, ExternalLink, Pencil } from 'lucide-react';
import VideoModal from './VideoModal';
import { uploadVideo } from '../services/uploadService';
import '../styles/VideoGallery.css';

export interface Video {
  _id: string;
  title: string;
  url: string; // YouTube, Vimeo URL or direct mp4
  type: 'youtube' | 'vimeo' | 'mp4' | 'other';
  thumbnail?: string;
  createdAt: string;
}

interface VideoGalleryProps {
  workspaceId: string;
  entityType: string;
  entityId: string;
  videos: Video[];
  onAddVideo: (video: Omit<Video, '_id' | 'createdAt'>) => Promise<void>;
  onDeleteVideo: (videoId: string) => Promise<void>;
  onUpdateVideo?: (videoId: string, title: string) => Promise<void>;
  hideHeader?: boolean;
  onAddClick?: () => void;
}

export interface VideoGalleryHandle {
  openAddDialog: () => void;
}

const VideoGallery: React.ForwardRefRenderFunction<VideoGalleryHandle, VideoGalleryProps> = ({
  workspaceId,
  entityType,
  entityId,
  videos,
  onAddVideo,
  onDeleteVideo,
  onUpdateVideo,
  hideHeader = false,
  onAddClick
}, ref) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState<Video | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [updating, setUpdating] = useState(false);

  // Expose openAddDialog method to parent via ref
  React.useImperativeHandle(ref, () => ({
    openAddDialog: () => {
      setShowAddDialog(true);
    }
  }));
  const [activeTab, setActiveTab] = useState<'url' | 'file'>('url');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Extract video ID and type from URL
  const parseVideoUrl = (url: string): { type: Video['type']; videoId: string; thumbnail: string } | null => {
    // YouTube patterns
    const youtubePatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/
    ];

    for (const pattern of youtubePatterns) {
      const match = url.match(pattern);
      if (match) {
        const videoId = match[1];
        return {
          type: 'youtube',
          videoId,
          thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        };
      }
    }

    // Vimeo pattern
    const vimeoPattern = /vimeo\.com\/(?:video\/)?(\d+)/;
    const vimeoMatch = url.match(vimeoPattern);
    if (vimeoMatch) {
      return {
        type: 'vimeo',
        videoId: vimeoMatch[1],
        thumbnail: '' // Will be fetched from Vimeo API if needed
      };
    }

    // Direct MP4
    if (url.toLowerCase().endsWith('.mp4')) {
      return {
        type: 'mp4',
        videoId: url,
        thumbnail: ''
      };
    }

    return {
      type: 'other',
      videoId: url,
      thumbnail: ''
    };
  };

  const handleAddVideo = async () => {
    if (!videoUrl.trim()) {
      setUploadError('Please enter a video URL');
      return;
    }

    const parsedVideo = parseVideoUrl(videoUrl);
    if (!parsedVideo) {
      setUploadError('Invalid video URL. Please enter a valid YouTube, Vimeo, or MP4 URL');
      return;
    }

    try {
      setAdding(true);
      setUploadError('');
      await onAddVideo({
        title: videoTitle.trim() || 'Untitled Video',
        url: videoUrl.trim(),
        type: parsedVideo.type,
        thumbnail: parsedVideo.thumbnail
      });

      // Reset form
      setVideoUrl('');
      setVideoTitle('');
      setShowAddDialog(false);
      // Don't show toast here, let parent handle it
    } catch (error: any) {
      console.error('Error adding video:', error);
      setUploadError(error.message || 'Failed to add video');
    } finally {
      setAdding(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setUploadError('Please select a valid video file');
      return;
    }

    // Validate file size (max 500MB)
    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file.size > maxSize) {
      setUploadError('Video file size must be less than 500MB');
      return;
    }

    try {
      setAdding(true);
      setUploadError('');
      setUploadProgress(0);

      const uploadedVideo = await uploadVideo(
        workspaceId,
        entityId,
        file,
        `${entityType}-video`,
        entityType,
        (progress: number) => {
          setUploadProgress(progress);
        }
      );

      await onAddVideo({
        title: videoTitle.trim() || file.name.replace(/\.[^/.]+$/, ''), // Use filename without extension as default title
        url: uploadedVideo.path, // Store S3 path, not presigned URL
        type: 'mp4',
        thumbnail: ''
      });

      // Reset form
      setVideoUrl('');
      setVideoTitle('');
      setShowAddDialog(false);
      setUploadProgress(0);
      // Don't show toast here, let parent handle it
    } catch (err: any) {
      console.error('Error uploading video:', err);
      setUploadError(err.message || 'Failed to upload video');
    } finally {
      setAdding(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await onDeleteVideo(videoId);
      setDeleteConfirm(null);
    } catch (error: any) {
      console.error('Error deleting video:', error);
      toast.info('Failed to delete video');
    }
  };

  const handleEditClick = (e: React.MouseEvent, video: Video) => {
    e.stopPropagation();
    setEditingVideo(video);
    setEditTitle(video.title);
  };

  const handleUpdateTitle = async () => {
    if (!editingVideo || !onUpdateVideo) return;

    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle) {
      toast.error('Title cannot be empty');
      return;
    }

    try {
      setUpdating(true);
      await onUpdateVideo(editingVideo._id, trimmedTitle);
      setEditingVideo(null);
      setEditTitle('');
      toast.success('Video title updated');
    } catch (error: any) {
      console.error('Error updating video:', error);
      toast.error('Failed to update video title');
    } finally {
      setUpdating(false);
    }
  };

  const getVideoThumbnail = (video: Video): string => {
    if (video.thumbnail) {
      return video.thumbnail;
    }

    // Generate thumbnail based on type
    if (video.type === 'youtube') {
      const match = video.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (match) {
        return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
      }
    }

    return '';
  };

  const handleAddButtonClick = () => {
    setShowAddDialog(true);
    if (onAddClick) {
      onAddClick();
    }
  };

  return (
    <div className="video-gallery">
      {!hideHeader && (
        <div className="video-gallery-header">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleAddButtonClick}
          >
            <LinkIcon size={16} />
            Add Video
          </button>
        </div>
      )}

      {videos.length === 0 ? (
        <div className="video-gallery-empty">
          <Play size={48} style={{ color: '#9CA3AF', marginBottom: '12px' }} />
          <p>No videos found.</p>
          <p style={{ fontSize: '13px', color: '#9CA3AF' }}>
            Add YouTube, Vimeo links or MP4 files to showcase your videos
          </p>
        </div>
      ) : (
        <div className="video-gallery-grid">
          {videos.map((video) => (
            <div key={video._id} className="video-gallery-item">
              <div
                className="video-thumbnail"
                onClick={() => setSelectedVideo(video)}
              >
                {getVideoThumbnail(video) ? (
                  <img src={getVideoThumbnail(video)} alt={video.title} />
                ) : (
                  <div className="video-thumbnail-placeholder">
                    <Play size={48} />
                  </div>
                )}
                <div className="video-play-overlay">
                  <div className="video-play-button">
                    <Play size={32} />
                  </div>
                </div>
                <div className="video-type-badge">{video.type.toUpperCase()}</div>
              </div>
              <div className="video-info">
                <h4 className="video-title" title={video.title}>{video.title}</h4>
                <div className="video-actions">
                  <button
                    type="button"
                    className="video-action-btn"
                    onClick={(e) => handleEditClick(e, video)}
                    title="Edit title"
                  >
                    <Pencil size={14} />
                  </button>
                  <a
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="video-action-btn"
                    onClick={(e) => e.stopPropagation()}
                    title="Open in new tab"
                  >
                    <ExternalLink size={14} />
                  </a>
                  <button
                    type="button"
                    className="video-action-btn video-action-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(video._id);
                    }}
                    title="Delete video"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Video Dialog */}
      {showAddDialog && (
        <div className="video-modal-overlay">
          <div className="video-modal-content video-add-dialog">
            <div className="video-modal-header">
              <h3>Add Video</h3>
              <button
                type="button"
                className="video-modal-close"
                onClick={() => {
                  setShowAddDialog(false);
                  setUploadError('');
                  setVideoUrl('');
                  setVideoTitle('');
                  setActiveTab('url');
                }}
                disabled={adding}
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="video-upload-tabs">
              <button
                type="button"
                className={`video-upload-tab ${activeTab === 'url' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('url');
                  setUploadError('');
                }}
                disabled={adding}
              >
                <LinkIcon size={18} />
                Video URL
              </button>
              <button
                type="button"
                className={`video-upload-tab ${activeTab === 'file' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('file');
                  setUploadError('');
                }}
                disabled={adding}
              >
                <Upload size={18} />
                Upload File
              </button>
            </div>

            <div className="video-modal-body">
              {uploadError && (
                <div className="error-message" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '6px', fontSize: '14px' }}>
                  {uploadError}
                </div>
              )}

              {/* Title field (common for both tabs) */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Title (Optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter video title"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  disabled={adding}
                />
              </div>

              {activeTab === 'url' ? (
                <div className="form-group">
                  <label className="form-label">Video URL *</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                    value={videoUrl}
                    onChange={(e) => {
                      setVideoUrl(e.target.value);
                      setUploadError('');
                    }}
                    disabled={adding}
                  />
                  <p className="form-help-text">
                    Supported formats:
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>YouTube: https://www.youtube.com/watch?v=VIDEO_ID</li>
                      <li>Vimeo: https://vimeo.com/VIDEO_ID</li>
                      <li>Direct MP4: https://example.com/video.mp4</li>
                    </ul>
                  </p>
                </div>
              ) : (
                <div className="video-file-upload">
                  <div
                    className="video-upload-dropzone"
                    onClick={() => !adding && document.getElementById('video-file-input-gallery')?.click()}
                    style={{
                      border: '2px dashed #D1D5DB',
                      borderRadius: '8px',
                      padding: '40px 20px',
                      textAlign: 'center',
                      cursor: adding ? 'not-allowed' : 'pointer',
                      backgroundColor: adding ? '#F9FAFB' : '#FFFFFF',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Upload size={48} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                      Click to upload video file
                    </p>
                    <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                      MP4, WebM, or other video formats (max 500MB)
                    </p>
                    {adding && uploadProgress > 0 && (
                      <div className="upload-progress" style={{ maxWidth: '300px', margin: '0 auto' }}>
                        <div className="progress-bar" style={{ height: '8px', backgroundColor: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
                          <div
                            className="progress-fill"
                            style={{ width: `${uploadProgress}%`, height: '100%', backgroundColor: '#48182F', transition: 'width 0.3s' }}
                          />
                        </div>
                        <span className="progress-text" style={{ display: 'block', marginTop: '8px', fontSize: '14px', color: '#6B7280' }}>
                          Uploading... {uploadProgress}%
                        </span>
                      </div>
                    )}
                  </div>
                  <input
                    id="video-file-input-gallery"
                    type="file"
                    accept="video/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    disabled={adding}
                  />
                </div>
              )}
            </div>
            <div className="video-modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={() => {
                  setShowAddDialog(false);
                  setUploadError('');
                  setVideoUrl('');
                  setVideoTitle('');
                  setActiveTab('url');
                }}
                disabled={adding}
              >
                Cancel
              </button>
              {activeTab === 'url' && (
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  onClick={handleAddVideo}
                  disabled={adding || !videoUrl.trim()}
                >
                  {adding ? 'Adding...' : 'Add Video'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoModal
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="video-modal-overlay">
          <div className="video-modal-content video-confirm-dialog">
            <div className="video-modal-header">
              <h3>Delete Video</h3>
              <button
                type="button"
                className="video-modal-close"
                onClick={() => setDeleteConfirm(null)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="video-modal-body">
              <p>Are you sure you want to delete this video? This action cannot be undone.</p>
            </div>
            <div className="video-modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger btn-md"
                onClick={() => handleDeleteVideo(deleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Title Dialog */}
      {editingVideo && (
        <div className="video-modal-overlay">
          <div className="video-modal-content video-confirm-dialog">
            <div className="video-modal-header">
              <h3>Edit Video Title</h3>
              <button
                type="button"
                className="video-modal-close"
                onClick={() => {
                  setEditingVideo(null);
                  setEditTitle('');
                }}
                disabled={updating}
              >
                <X size={20} />
              </button>
            </div>
            <div className="video-modal-body">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Enter video title"
                  disabled={updating}
                  autoFocus
                />
              </div>
            </div>
            <div className="video-modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={() => {
                  setEditingVideo(null);
                  setEditTitle('');
                }}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-md"
                onClick={handleUpdateTitle}
                disabled={updating || !editTitle.trim()}
              >
                {updating ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.forwardRef(VideoGallery);
